import { useEffect, useMemo, useState } from 'react'

import { themeEdited } from 'constants/messages'

import { ITheme } from 'store/theme/types'

import { useSnackbar } from 'contexts/SnackbarContext'
import { FormProvider, useForm } from 'react-hook-form'

import { Stack, Box, Typography } from '@mui/material'
import { isEqual } from 'lodash'

import { Button, Tabs, MenuButton, MenuButtonItem, Tooltip, tabRuleVariant, TAB_RULE_LIFT, previewControlsPlacement } from '@repo/ui'
import { Header } from 'components'
import { ResizableSplit } from 'components/layout/ResizableSplit'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { QueryErrorState } from 'components/feedback/QueryErrorState/QueryErrorState'
import { StickyFormHeader } from 'components/navigation/StickyFormHeader/StickyFormHeader'

import {
  ThemeForm,
  Preview,
  EmailPreview,
  LogoUploadCard,
  WhiteLabelCard,
  StudioSkeleton,
  PreviewControls,
  APP_FLOW_PAGES,
  DEVICE_OPTIONS,
  DeviceKey,
  type HighlightPartCount,
  type StudioTokenName,
} from './components'
import { useGetCompanyStyles } from 'hooks/api/useGetCompanyStyles'
import { useProducts } from 'hooks/api/useProducts'
import type { StudioPreviewProduct } from '@repo/types'
import { useDefaultInstallation, useCurrentInstallation } from 'hooks/api/useInstallations'
import { useUpdateCompanySettings } from 'hooks/api/useUpdateCompanySettings'
import { useCurrentUserCompany } from 'hooks/api/useCompanyQuery'
import { useGetEmailTemplates } from 'hooks/api/useEmailStudio'

const STUDIO_TABS = [
  { label: 'Certificate', value: 'certificate' },
  { label: 'Email', value: 'email' },
]

/**
 * The style fields this editor owns — the ONE list, used both to seed the form and to decide
 * whether anything is unsaved. Anything not named here (`logo`, and any column the API grows
 * later) is not the editor's and never makes the form dirty.
 */
/**
 * The editor column: rests level with the preview.
 *
 * `mt` is the same 16px the preview pane rests at, so the two columns START level — both at
 * viewport 162.5 (the chrome block's bottom 146.5 + 16). It is a MARGIN and not the sticky
 * header's padding, which is the distinction that lets the two sides differ: this scrolls
 * away with the content while the pinned preview keeps its air. The header's padding was a
 * full-width band and could only ever apply to both.
 *
 * DO NOT RE-ADD THE `mask-attachment: fixed` FADE. It shipped here and produced the exact
 * defect it was meant to avoid: a ~146px black band under the tab row, at rest, with the
 * first card's whole header ("Logo" / "Manage logos") invisible inside it.
 *
 * The idea was to mask the CARDS to transparent as they approach the tab row, anchoring the
 * gradient to the VIEWPORT so a fixed point on SCREEN fades rather than a fixed point in the
 * document. That anchoring does not exist: `mask-attachment` was dropped from CSS Masking and
 * Chrome implements NEITHER spelling. Measured on Chrome 148 —
 *
 *   CSS.supports('mask-attachment', 'fixed')          false
 *   CSS.supports('-webkit-mask-attachment', 'fixed')  false
 *   el.style.cssText after setting both               "" (both silently discarded)
 *
 * so the declaration vanished and the gradient fell back to element-relative: transparent
 * over this column's own first 146px, opaque at 170, at EVERY scroll position including rest.
 *
 * It passed review because the failure is invisible to the DOM. `getBoundingClientRect` still
 * reported the columns level at 162.5/162.5 — a mask changes what is painted, never the box —
 * so "columns rest level" measured true while the top 146px of the column rendered as page
 * background. Geometry assertions cannot see this; only a screenshot can.
 *
 * Without a viewport-anchored mask the fade needs this column to be its own scroll container,
 * which `StickyFormHeader`'s docblock already rules out as a structural change to every page
 * using it — and that same block records the decision this now follows: the boundary is a
 * hard cut against the opaque header plus the tab row's hairline, not a fade.
 */
const EDITOR_COLUMN_SX = {
  mt: '16px',
} as const

const EDITABLE_STYLE_KEYS = [
  'primary_color',
  'secondary_color',
  'text_color',
  'bg_color',
  'border_radius',
  'font',
  'title_font',
  'logo_align',
  'logo_size',
  'email_logo_variant',
  'certificate_logo_variant',
] as const satisfies readonly (keyof ITheme)[]

export const StudioPage = () => {
  const methods = useForm()
  const { open: notification } = useSnackbar()

  const updateCompanySettingsMutation = useUpdateCompanySettings()

  // The current admin's own company — the theme set here is stored on this
  // company object and inherited by its Vera app installation (VERA-273).
  const {
    data: company,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    error: companyError,
    refetch: refetchCompany,
  } = useCurrentUserCompany()
  const companyId = company?.id

  const {
    data: companyStylesResponse,
    isLoading: isCompanySettingsLoading,
    isError: isStylesError,
    error: stylesError,
    refetch: refetchStyles,
  } = useGetCompanyStyles({ companyId })
  const companyStyles = useMemo(() => companyStylesResponse?.styles, [companyStylesResponse])

  const { reset, watch } = methods
  const formValues = watch()

  // Studio shell: which tab + which app flow / device the preview shows (VERA-268).
  const [activeTab, setActiveTab] = useState('certificate')
  const [previewPage, setPreviewPage] = useState<MenuButtonItem>(APP_FLOW_PAGES[1])

  /*
    Where the preview is pretending to be served from. Built from the brand's OWN
    installs rather than an abstract hosted/embedded switch, so the caption names a real
    address: the difference it makes (the brand header) is then obviously about where
    the page lives, not a style choice.

    DEFAULT is the Vera-hosted subdomain; MANUAL and ECOMMERCE are embeds on the brand's
    own site, where their header already carries their logo. A brand with no embed sees
    one entry and no dropdown.
  */
  const { data: defaultInstallation } = useDefaultInstallation()
  // `useCurrentInstallation` resolves the MANUAL install — the snippet on the brand's own
  // site. NOT `usePrimaryInstallation`: primary is usually the hosted DEFAULT row, so it
  // returns the same record as the line above and the dropdown collapses to one entry.
  const { data: embedInstallation } = useCurrentInstallation()
  const installOptions = useMemo(() => {
    const opts: { label: string; embedded: boolean }[] = []
    const hosted = defaultInstallation?.domain || defaultInstallation?.installation_url
    if (hosted) opts.push({ label: hosted, embedded: false })
    const embedLabel = embedInstallation?.domain || embedInstallation?.installation_url
    if (embedLabel) opts.push({ label: embedLabel, embedded: true })
    return opts
  }, [defaultInstallation, embedInstallation])

  const [installLabel, setInstallLabel] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!installLabel && installOptions.length) setInstallLabel(installOptions[0].label)
  }, [installOptions, installLabel])
  const previewEmbedded =
    installOptions.find(o => o.label === installLabel)?.embedded ?? false
  const [device, setDevice] = useState<MenuButtonItem>(DEVICE_OPTIONS[0])

  /*
    VRG-827 — which of the brand's OWN products the preview renders.

    `limit: 100` rather than a page: this feeds a searchable picker, and paginating a
    dropdown means a brand's 101st product is unreachable from Studio with no hint that
    it exists. 100 covers the brands this surface is used by; past that the picker's
    search is the affordance, and a `search`-as-you-type endpoint would be the fix
    rather than a page control nobody would find here.

    Deliberately NOT gated on `activeTab === 'certificate'`: the query is cached for
    five minutes by `useProducts`, so fetching it once on mount costs one request and
    makes switching back to the certificate tab instant, where gating would re-fetch.
  */
  const productsQuery = useProducts({ company_id: companyId, limit: 100 }, { enabled: !!companyId })
  // Memoised on the query's own data, not written inline: `?? []` mints a NEW array
  // every render, which would make both memos below recompute on every keystroke in the
  // theme form — the one thing a live preview must not do while a colour is being dragged.
  const previewProducts = useMemo(() => productsQuery.data?.data ?? [], [productsQuery.data])

  const productOptions: MenuButtonItem[] = useMemo(
    () =>
      previewProducts
        // A product with no title would render a blank row that cannot be identified
        // or searched for. Falling back to the SKU keeps it selectable; a product with
        // neither is dropped rather than shown as an empty line.
        .map(p => ({ label: p.title || p.sku || '', value: p.id }))
        .filter(o => o.label !== ''),
    [previewProducts],
  )

  // `null` until the list lands. The effect below promotes the first product once it
  // does — "just first one on product", and the brand can then pick another.
  const [productItem, setProductItem] = useState<MenuButtonItem | null>(null)
  useEffect(() => {
    // Only SEED. Re-running on every list change would yank the operator's own
    // selection back to the first product whenever the query refetched.
    if (!productItem && productOptions.length > 0) setProductItem(productOptions[0])
  }, [productOptions, productItem])

  /*
    The display fields the bridge carries, and nothing else — see `StudioPreviewProduct`.
    A theming preview has no business shipping price, stock or owner data into an iframe
    that is, by construction, rendering fabricated provenance around it.
  */
  const previewProduct: StudioPreviewProduct | null = useMemo(() => {
    const row = previewProducts.find(p => p.id === productItem?.value)
    if (!row) return null
    return {
      id: row.id,
      title: row.title,
      sku: row.sku,
      images: row.images,
      description: row.description,
      details: row.details,
      type: row.type,
    }
  }, [previewProducts, productItem])

  // Editor | preview split ratio (draggable divider), shared across tabs.
  const [splitPct, setSplitPct] = useState(58)

  /*
    VRG-549 — "what does this control edit?".

    ONE token at a time, held here rather than in `ThemeForm`, because the answer is
    rendered in two places that do not know about each other: the panel under the control
    and the outline inside the preview. Two panels open at once would each claim a
    highlight and only the last would be telling the truth, so opening one closes the
    other by construction.

    `impactParts` is what the live preview REPORTED it outlined — measured in the app (or
    in the email document) and sent back, never asserted here. Cleared on every change of
    token or tab so a previous surface's counts cannot linger under a new question; the
    preview answers again within a frame.
  */
  const [impactToken, setImpactToken] = useState<StudioTokenName | null>(null)
  const EMPTY_IMPACT: { parts: HighlightPartCount[]; invisible: number } = {
    parts: [],
    invisible: 0,
  }
  const [impact, setImpact] = useState(EMPTY_IMPACT)
  const toggleImpact = (token: StudioTokenName) => {
    setImpact(EMPTY_IMPACT)
    setImpactToken((current) => (current === token ? null : token))
  }
  useEffect(() => {
    setImpact(EMPTY_IMPACT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // VERA-644 (TD-066 last-mile): the certificate-tab WhiteLabelCard's live
  // (possibly unsaved) "Hide Powered by Vera" toggle, mirrored here so the
  // iframed certificate preview can reflect it immediately.
  const [previewHidePoweredBy, setPreviewHidePoweredBy] = useState(false)

  // Email tab: brand-customer templates + the selected one for the preview (VERA-270).
  const { data: emailTemplates } = useGetEmailTemplates()
  // Sorted by category so the dropdown groups templates under category
  // subheaders (Sign in / Onboarding / Transfers / …), mirroring Internal Labs.
  const emailTemplateOptions: MenuButtonItem[] = useMemo(
    () =>
      [...(emailTemplates ?? [])]
        .sort(
          (a, b) =>
            a.category.localeCompare(b.category) || a.name.localeCompare(b.name),
        )
        .map((t) => ({ label: t.name, value: t.id, group: t.category })),
    [emailTemplates],
  )
  const [emailTemplate, setEmailTemplate] = useState<MenuButtonItem | null>(null)
  useEffect(() => {
    if (!emailTemplate && emailTemplateOptions.length) setEmailTemplate(emailTemplateOptions[0])
  }, [emailTemplate, emailTemplateOptions])
  const selectedEmailTemplate = useMemo(
    () => (emailTemplates ?? []).find((t) => t.id === emailTemplate?.value),
    [emailTemplates, emailTemplate],
  )

  const hasChanges = useMemo(() => {
    if (!companyStyles) return !isEqual(formValues, companyStyles)
    /*
      Compare the two sides FIELD BY FIELD over `EDITABLE_STYLE_KEYS`, rather than comparing
      the objects whole. Whole-object equality has been wrong here twice, in both directions,
      because the two sides never have the same key set:

        VERA-304  the payload carried a key the form cannot have. `logo` is merged in
                  server-side from `companies.logo` and is never registered, so `isEqual`
                  saw an extra key and reported dirty on load, for every company, forever.
                  Fixed by deleting `logo` from the payload side.

        VRG-221   the form carries keys the payload does not. The seed effect below always
                  resets all 11 fields, so any field absent from the API response is present
                  in `formValues` as `undefined` — and lodash counts own keys, so
                  `isEqual({a:1,b:undefined},{a:1})` is FALSE. Measured on the seeded
                  company, whose stored styles hold 6 of the 11: `text_color`, `logo_align`,
                  `logo_size`, `email_logo_variant` and `certificate_logo_variant` are all
                  missing, so the form was dirty on load and Save was enabled with nothing to
                  save. It survived VERA-304 because that fix only trimmed the other side.

      Picking a fixed key list off BOTH sides is what makes it symmetric: an unknown key on
      either side is now ignored by construction rather than by a delete that has to be kept
      in step. `?? null` collapses "absent" and "undefined" so those five stop counting as
      edits. Values here are all scalars (colors, font names, a radius string, the align /
      size / variant enums), so a plain per-key comparison is sufficient.
    */
    return EDITABLE_STYLE_KEYS.some(
      (key) => (formValues[key] ?? null) !== (companyStyles[key] ?? null),
    )
  }, [formValues, companyStyles])

  const onSubmit = async () => {
    const themesData = {
      primary_color: formValues.primary_color || companyStyles?.primary_color,
      secondary_color: formValues.secondary_color || companyStyles?.secondary_color,
      text_color: formValues.text_color || companyStyles?.text_color,
      bg_color: formValues.bg_color || companyStyles?.bg_color,
      border_radius: formValues.border_radius || companyStyles?.border_radius,
      font: formValues.font || companyStyles?.font,
      title_font: formValues.title_font || companyStyles?.title_font,
      logo_align: formValues.logo_align || companyStyles?.logo_align,
      logo_size: formValues.logo_size || companyStyles?.logo_size,
      email_logo_variant: formValues.email_logo_variant || companyStyles?.email_logo_variant,
      certificate_logo_variant:
        formValues.certificate_logo_variant || companyStyles?.certificate_logo_variant,
    } as ITheme

    if (!companyId) return
    updateCompanySettingsMutation.mutate({ companyId, settings: { styles: themesData } })

    notification({
      type: 'success',
      title: themeEdited,
    })
  }

  /**
   * The saved theme, in the form's own shape — the editor's fields and nothing else.
   *
   * One source for both seeding on load and reverting on Cancel. They were separate: the
   * effect below spelled out all 11 fields while Cancel did `reset(companyStyles)`, which
   * reverts to a DIFFERENT object — one carrying `logo` (not a form field) and missing any
   * style the API omits. Two ways to populate one form is how they drift.
   */
  const savedFormValues = useMemo(
    () =>
      Object.fromEntries(
        EDITABLE_STYLE_KEYS.map((key) => [key, companyStyles?.[key]]),
      ) as Partial<ITheme>,
    [companyStyles],
  )

  const onCancel = () => {
    reset(savedFormValues)
  }

  useEffect(() => {
    reset(savedFormValues)
  }, [savedFormValues])

  const isLoading = isCompanyLoading || (!!companyId && (isCompanySettingsLoading || !companyStyles))

  /*
    VRG-566 — BEFORE the loading branch, because the loading branch cannot end on its own.

    `isLoading` above includes `!companyStyles`, so once the style fetch FAILS the query
    settles, `isCompanySettingsLoading` goes false, `companyStyles` stays undefined — and
    the expression is still true. The skeleton then renders forever, indistinguishable
    from a slow load, with nothing on screen to suggest the request is never coming back.
    Live case: a company with no `companies_settings` row 400s here, and Studio simply
    never resolved.

    §19 is the principle — a skeleton is the LOADING state, and a settled failure is not
    loading. `InternalSupportRequestPage` already documents the identical trap in the
    identical words ("stays true forever on a 403 or a 404"), which is why this reuses its
    `QueryErrorState` (§28/§47) rather than hand-rolling a message: that component already
    splits 403 from everything else, names the missing permission, and offers the retry.

    AND `&& !data`, NOT a bare `isError` — the distinction matters on THIS page more than
    on the list pages the pattern came from. react-query keeps the last good `data` when a
    BACKGROUND refetch fails, so a bare `isError` would tear a working editor down to an
    error screen because a refetch blipped — discarding the user's unsaved theme edits
    with it. The honest question is not "did a request fail" but "is there anything to
    show", so the error state is reserved for the case where the answer is no. A failed
    refetch that still has data leaves the editor alone, which is what §19's own
    "don't flash over stale data" instinct implies.
  */
  const companyFailed = isCompanyError && !company
  const stylesFailed = isStylesError && !companyStyles

  if (companyFailed || stylesFailed) {
    return (
      <>
        <Header title="Studio" />
        <QueryErrorState
          error={companyFailed ? companyError : stylesError}
          subject={companyFailed ? 'your company' : 'your theme'}
          permissionLabel="View company"
          onRetry={() => void (companyFailed ? refetchCompany() : refetchStyles())}
        />
      </>
    )
  }

  if (isLoading) return <StudioSkeleton />

  // Two DIFFERENT failures, and they used to share one message.
  //
  // The guard was `if (!company || !companyStyles)` printing "No company is
  // associated with your account yet" — so a company that exists but has no
  // `companies_settings` row reported a missing COMPANY. Hit live on 2026-09-03:
  // the dev company account resolves to Example Co, which has a `company_users`
  // link and no settings row, and the page blamed the account. Whoever reads
  // that goes looking at permissions and membership, which are both fine.
  //
  // Keep them separate: the reader can only act on the one that is true.
  if (!company) {
    return (
      <>
        <Header title="Studio" />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          No company is associated with your account yet, so there's no theme to edit.
        </Typography>
      </>
    )
  }

  if (!companyStyles) {
    return (
      <>
        <Header title="Studio" />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          {company.name} doesn't have a theme yet. It gets one the first time its
          branding is saved.
        </Typography>
      </>
    )
  }

  return (
    <>
      {/* THE WHOLE EDITOR CHROME PINS: title + Cancel/Save + the tab row + the preview chips.
          `StickyFormHeader`, not a second sticky implementation (§28/§36) — its `children` slot
          renders INSIDE the sticky block, which is exactly where a tab row goes (the support
          lists put their Search/Filter/Sort row there). It also owns the two things that are
          easy to get wrong: resolving the real scroll parent (the app scrolls inside `<main>`,
          not the window) and masking the scrollport's 24px top padding so cards are not seen
          sliding through the band above the title.

          Cancel + Save moved here from a bar pinned to the viewport bottom. That bar was
          permanent chrome that carried no state: it rendered whether or not anything was
          unsaved (`hasChanges` only ever DISABLED the button), and it was full-bleed to the
          viewport edge (measured right edge 1280) while Studio's content column ends at 1256,
          so its right edge aligned with nothing. Order is §26/§33: one primary, rightmost, so
          Cancel (secondary) then Save. `Header` owns the 12px gap and the 40px height.

          THE CHIPS PIN TOO. They drive what the preview shows, and the preview is itself
          fixed — a control that scrolls away from the thing it controls is the worse option.
          Structurally they are already absolutely positioned OVER the tab row, so leaving them
          behind would mean inventing a separate anchor for them.

          THE BUDGET, measured at 1280x800: the pinned block runs 0..162.5 (its own 24..162.5
          plus the 24px scrollport band its `::before` masks), so 162.5px of the 800px viewport
          — 20.3% — is now permanent chrome. That is under the support list's 231px/29% for the
          equivalent block. It is also bought with the 76px reclaimed from the removed save bar,
          so the scrollable editor column nets out 76px AHEAD of where it started, not behind.

          `stuckEdge={false}` because the tab row below already draws a full-width border.
          With both, scrolling produced TWO rules 17px apart — y=145.5 and y=162.5, compositing
          to rgb(39,39,40) and rgb(39,39,42) against the page background: same weight,
          indistinguishable colour, reading as a rendering fault rather than two deliberate
          edges. The block yields rather than the tabs, because the tab row's border is present
          at rest too and belongs to the tabs, not to the scroll state. Do NOT instead drop the
          Tabs' own border. */}
      <StickyFormHeader
        // 0, not the default 16px. The tab row below is this block's last row and it
        // draws its own border, so that padding was not breathing room — it was an
        // opaque band of page background between the tab border and the first card,
        // reading as a black bar across the full page width (including over the
        // preview, which does not scroll and so has nothing to mask). Removing it
        // shortens the block by 16px, which is why STUDIO_PANE_STICKY_TOP and
        // STUDIO_PANE_OFFSET move by the same 16 — see previewConfig/Preview.
        bottomGap={0}
        title="Studio"
        // On the LIVE header only. The other `title="Studio"` in this file is the
        // no-company error branch, where an offer to define the editor is beside the point.
        titleAdornment={<InfoTooltip term="studio" />}
        stuckEdge={false}
        actions={
          <>
            <Button variant="secondary" disableElevation onClick={() => onCancel()}>
              Cancel
            </Button>
            {/*
              VRG-776 — say why it is disabled. NOT `FormSubmitButton`, despite this page
              sitting inside a `FormProvider`: that component gates on RHF's `isDirty`,
              while Studio gates on its own `hasChanges` memo, which is a deliberate
              comparison against the SAVED settings rather than against the form's mount
              state. Swapping it would quietly change when Save is live.

              The wrapping span is what makes the tooltip work at all — a disabled MUI
              button sets `pointer-events: none` and never fires hover.
            */}
            <Tooltip
              title={
                updateCompanySettingsMutation.isPending
                  ? 'Saving…'
                  : hasChanges
                    ? ''
                    : 'No changes to save'
              }
            >
              <span style={{ display: 'inline-flex' }}>
                <Button
                  disableElevation
                  onClick={onSubmit}
                  disabled={!hasChanges || updateCompanySettingsMutation.isPending}
                  loading={updateCompanySettingsMutation.isPending}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
          </>
        }
      >
        <Box data-tour="studio-template-tabs" sx={{ position: 'relative', width: '100%' }}>
          {/* Preview controls — right-aligned with the tabs (VERA-268 / VERA-270).

              TEMPORARY (VRG-470): Studio is the surface this rule question came from, and its
              controls are NOT `Tabs`' `rightComponents` — they are absolutely positioned OVER the
              tab row by this page, so the shared component cannot lift them. At `top: 6` with
              `height: 40` their bottom sits at 46 while the 52px row's rule sits at 52: a 6px gap,
              which is why the rule reads as their underline.

              Variant 3 raises them so the gap becomes TAB_RULE_LIFT. Variants 1 and 2 leave the
              original `top: 6` untouched. Remove with the flag. */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{
              position: 'absolute',
              right: 0,
              top: tabRuleVariant() === 3 ? 6 - TAB_RULE_LIFT : 6,
              height: 40,
              zIndex: 2,
              // Axis C variant 2 moves these into the preview pane's control band; they are
              // rendered there instead (see `previewControls` below). Hidden rather than
              // removed so the surrounding markup stays identical between the two variants
              // and the comparison is of PLACEMENT only.
              display: previewControlsPlacement() === 2 ? 'none' : 'flex',
            }}
          >
            {activeTab === 'certificate' ? (
              <PreviewControls
                sized
                previewPage={previewPage}
                onPreviewPageChange={setPreviewPage}
                device={device}
                onDeviceChange={setDevice}
                productOptions={productOptions}
                product={productItem}
                onProductChange={setProductItem}
              />
            ) : (
              <>
                {emailTemplate && (
                <MenuButton
                  key="studio-email-template"
                  sx={{ minWidth: 220 }}
                  selected={emailTemplate}
                  items={emailTemplateOptions}
                  onSelect={setEmailTemplate}
                  variant="secondary"
                  search
                />
                )}
              </>
            )}
          </Stack>

          {/* The shared `Tabs`, rendered with NO children so it emits only the tab LIST — the
              panels are the conditional split below. `Tabs` wraps list + children in one
              `TabContext`, so a `TabPanel` cannot live outside it; splitting them that way
              would have meant hand-rolling the list and losing its styling (the gradient
              indicator, the first-tab padding fix). `activeTab` is already controlled state
              here, so a conditional render is equivalent — @mui/lab's TabPanel renders nothing
              for the inactive value anyway. */}
          <Tabs tabs={STUDIO_TABS} redirect={false} value={activeTab} onChange={setActiveTab} />
        </Box>
      </StickyFormHeader>

      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* No `pt` — StickyFormHeader's own `pb: 4` is the 16px the TabPanel used to add, so
            the split still starts at 161.5. */}
        <FormProvider {...methods}>
          {activeTab === 'certificate' ? (
            <Box>
              <ResizableSplit
                leftSize={splitPct}
                onLeftSizeChange={setSplitPct}
                // The Stack carries no `pb`. The 24px it used to have was clearance above the
                // save bar; the scroll container's own 24px bottom padding is now the page
                // gutter, and doubling them left the editor column ending 48px up while the
                // preview pane ends at 24 (measured).
                left={
                  <Stack spacing={4} sx={EDITOR_COLUMN_SX}>
                    <LogoUploadCard
                      surface="certificate"
                      markLogo={company.logo}
                      markLogoDark={company.logo_dark}
                      wordmarkLogo={company.logo_wide}
                      wordmarkLogoDark={company.logo_wide_dark}
                      subdomain={company.subdomain}
                    />
                    <ThemeForm
                      company_theme={companyStyles}
                      companyId={companyId}
                      impactToken={impactToken}
                      onToggleImpact={toggleImpact}
                      impactSurface="certificate"
                      impactParts={impact.parts}
                      impactInvisible={impact.invisible}
                      previewLabel={previewPage.label as string}
                    />
                    {/* Brand header sits ABOVE white label: it decides whether
                        the brand is named at all, which is the larger question,
                        and white label then decides whether Vera is named
                        underneath it. Certificate tab only — the bar is a
                        certificate-page surface and has no email equivalent. */}
                    <WhiteLabelCard surface="certificate" onHidePoweredByChange={setPreviewHidePoweredBy} />
                  </Stack>
                }
                right={
                  <Preview
                    formValues={formValues}
                    // VRG-809 — WHICH brand the preview renders. Without it the iframe loads the
                    // bare app origin and the app resolves to its hardcoded seed company, so every
                    // brand's Studio previewed the seed brand's logo and name.
                    companyId={company?.id}
                    previewPage={previewPage.value as string}
                    device={device.value as DeviceKey}
                    product={previewProduct}
                    hidePoweredBy={previewHidePoweredBy}
                    embedded={previewEmbedded}
                    installOptions={installOptions}
                    installLabel={installLabel}
                    onInstallChange={setInstallLabel}
                    highlightToken={impactToken}
                    onHighlightResult={setImpact}
                    // Axis C variant 2 (VRG-470): the SAME two MenuButtons, rendered in the
                    // preview's control band beside the zoom stepper instead of over the tab
                    // row. Same state, same handlers — only the placement differs.
                    controls={
                      previewControlsPlacement() === 2 ? (
                        // Unsized: this band sits beside the zoom stepper, so the menus
                        // size to their content. Same component as the tab-row placement
                        // (VRG-827) — the two copies of this markup are what made adding a
                        // third control a §36 extraction rather than an edit.
                        <PreviewControls
                          previewPage={previewPage}
                          onPreviewPageChange={setPreviewPage}
                          device={device}
                          onDeviceChange={setDevice}
                          productOptions={productOptions}
                          product={productItem}
                          onProductChange={setProductItem}
                        />
                      ) : undefined
                    }
                  />
                }
              />
            </Box>
          ) : (
            <Box>
              <ResizableSplit
                leftSize={splitPct}
                onLeftSizeChange={setSplitPct}
                // The Stack carries no `pb`. The 24px it used to have was clearance above the
                // save bar; the scroll container's own 24px bottom padding is now the page
                // gutter, and doubling them left the editor column ending 48px up while the
                // preview pane ends at 24 (measured).
                left={
                  <Stack spacing={4} sx={EDITOR_COLUMN_SX}>
                    <LogoUploadCard
                      surface="email"
                      markLogo={company.logo}
                      wordmarkLogo={company.logo_wide}
                    />
                    <ThemeForm
                      company_theme={companyStyles}
                      companyId={companyId}
                      impactToken={impactToken}
                      onToggleImpact={toggleImpact}
                      impactSurface="email"
                      impactParts={impact.parts}
                      impactInvisible={impact.invisible}
                      previewLabel={selectedEmailTemplate?.name}
                    />
                    <WhiteLabelCard surface="email" />
                  </Stack>
                }
                right={
                  <EmailPreview
                    formValues={formValues}
                    highlightToken={impactToken}
                    onHighlightResult={setImpact}
                    templateId={emailTemplate?.value as string | undefined}
                    name={selectedEmailTemplate?.name}
                    subject={selectedEmailTemplate?.subject}
                    description={selectedEmailTemplate?.description}
                    logo={company.logo}
                    sampleProps={selectedEmailTemplate?.sampleProps}
                  />
                }
              />
            </Box>
          )}
        </FormProvider>
      </Box>
    </>
  )
}
